import React, { useState, useEffect } from "react";
import {
  Box, Paper, Typography, Select, TextField, MenuItem, FormControl, InputLabel, Grid, Chip, LinearProgress
} from "@mui/material";
import Menu from "../../Menu";
import Header from "../../Header";
import { FormsApi } from "../formsUtils";
import FunnelDrillDialog from "./FunnelDrillDialog";
import Interessados from "./Interessados";

// Duas origens, e a resposta não guarda de onde veio: quem recebe o WhatsApp do robô também
// responde pelo link aberto. O que separa as duas é a submissão vinculada no candidato do robô,
// e a API já devolve os números prontos em `whatsapp` e `qr`.
//
// O QR não tem disparo nem rastreio de entrega. "Abriram o link" conta só quem chegou até o
// fim da identificação (termo, CPF, nome, cirurgião e WhatsApp): quem respondeu mais quem
// parou no meio, e esses ficam em "Não respondidos" com os dados que deram.
const BLOCOS = [
  {
    titulo: "WhatsApp",
    origem: "whatsapp",
    base: "sent",
    cartoes: [
      { chave: "sent", stage: "sent", label: "Enviados", cor: "#3f51b5" },
      { chave: "delivered", stage: "delivered", label: "Entregues", cor: "#5c6bc0" },
      { chave: "read", stage: "read", label: "Lidos", cor: "#7986cb" },
      { chave: "completed", stage: "completed_whatsapp", label: "Responderam", cor: "#2e7d32" },
      { chave: "notRead", stage: "not_read", label: "Não lidos", cor: "#e53935" },
      { chave: "notResponded", stage: "not_responded", label: "Não respondidos", cor: "#fb8c00" },
    ],
  },
  {
    titulo: "QR Code",
    origem: "qr",
    base: "opened",
    cartoes: [
      { chave: "opened", stage: "qr_opened", label: "Abriram o link", cor: "#3f51b5" },
      { chave: "completed", stage: "completed_qr", label: "Responderam", cor: "#2e7d32" },
      { chave: "notResponded", stage: "qr_not_responded", label: "Não respondidos", cor: "#fb8c00" },
    ],
  },
];

export default function Dashboard() {
  const [forms, setForms] = useState([]);
  const [formId, setFormId] = useState("");
  // Nasce com um período explícito: com o range vazio a tela consultava tudo enquanto os
  // campos apareciam preenchidos pelo navegador, e o painel mostrava o acumulado como se
  // estivesse filtrado. O padrão de 30 dias é o mesmo recorte do gráfico diário.
  const [range, setRange] = useState(() => {
    const hoje = new Date();
    const inicio = new Date();
    inicio.setDate(hoje.getDate() - 29);
    const iso = (d) => d.toISOString().slice(0, 10);
    return { from: iso(inicio), to: iso(hoje) };
  });
  const [data, setData] = useState(null);
  const [drill, setDrill] = useState({ open: false, stage: null, label: "" });

  // filtros atuais como query params (reaproveitados no drill-down)
  const drillQuery = [];
  if (formId) drillQuery.push(`form_id=${formId}`);
  if (range.from) drillQuery.push(`from=${range.from}`);
  if (range.to) drillQuery.push(`to=${range.to}`);

  const openDrill = (stage, label) => setDrill({ open: true, stage, label });

  useEffect(() => {
    FormsApi.list("?page=0&limit=200").then((resp) => setForms(resp?.data?.data || []), () => {});
  }, []);

  useEffect(() => {
    const params = [];
    if (formId) params.push(`form_id=${formId}`);
    if (range.from) params.push(`from=${range.from}`);
    if (range.to) params.push(`to=${range.to}`);
    const qs = params.length ? `?${params.join("&")}` : "";
    FormsApi.getDashboard(qs).then((resp) => setData(resp?.data || null), () => setData(null));
  }, [formId, range]);

  const maxSeries = Math.max(
    1,
    ...(((data && data.series && data.series.sent) || []).map((d) => d.total)),
    ...(((data && data.series && data.series.responses) || []).map((d) => d.total))
  );

  // une dias das duas séries
  const days = Array.from(new Set([
    ...(((data && data.series && data.series.sent) || []).map((d) => d.day)),
    ...(((data && data.series && data.series.responses) || []).map((d) => d.day)),
  ])).sort();
  const sentByDay = Object.fromEntries((((data && data.series && data.series.sent) || [])).map((d) => [d.day, d.total]));
  const respByDay = Object.fromEntries((((data && data.series && data.series.responses) || [])).map((d) => [d.day, d.total]));

  return (
    <div id="wrapper">
      <Menu />
      <div id="page-wrapper" className="gray-bg">
        <Header />
        <div className="row wrapper border-bottom white-bg page-heading">
          <div className="col-lg-8"><h2>Dashboard de Formulários</h2></div>
        </div>

        <div className="wrapper wrapper-content animated fadeInRight">
          {/* filtros */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel>Formulário</InputLabel>
              {/* Sem MenuProps o MUI dimensiona a lista pelo item mais longo e a desloca
                  para caber na tela, invadindo a barra lateral. Ancorada ao campo e com
                  largura limitada, o título longo trunca em vez de empurrar a lista. */}
              <Select label="Formulário" value={formId} onChange={(e) => setFormId(e.target.value)}
                MenuProps={{
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                  PaperProps: { sx: { maxWidth: 360, maxHeight: 320 } },
                }}>
                <MenuItem value=""><em>Todos</em></MenuItem>
                {forms.map((f) => <MenuItem key={f.id} value={f.id} sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.title}</MenuItem>)}
              </Select>
            </FormControl>
            {/* O período recorta bem convites e respostas. Já os envios do robô entram pelo
                created_at do candidato, porque ele não guarda a data do disparo: um lembrete
                conta no dia em que o paciente entrou no fluxo, não no dia em que a mensagem
                saiu. Fica exato quando o envio migrar para o convite, que tem sent_at. */}
            <TextField size="small" type="date" label="De" InputLabelProps={{ shrink: true }}
              value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
            <TextField size="small" type="date" label="Até" InputLabelProps={{ shrink: true }}
              value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
          </Paper>

          {BLOCOS.map((bloco) => {
            const numeros = (data && data[bloco.origem]) || {};
            const base = numeros[bloco.base] || 0;
            const porcento = (n) => (base > 0 ? Math.round((n / base) * 100) : 0);
            return (
              <Box key={bloco.titulo} sx={{ mb: 3 }}>
                <Typography sx={{
                  fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                  color: "#6b6c72", mb: 1,
                }}>
                  {bloco.titulo}
                </Typography>
                <Grid container spacing={2}>
                  {bloco.cartoes.map((cartao) => {
                    const valor = numeros[cartao.chave] || 0;
                    const clicavel = !!cartao.stage;
                    const mostraPorcento = base > 0 && valor <= base;
                    return (
                      <Grid item xs={12} sm={6} md={4} key={cartao.chave}>
                        <Paper variant="outlined"
                          onClick={clicavel ? () => openDrill(cartao.stage, `${bloco.titulo} · ${cartao.label}`) : undefined}
                          sx={{ p: 2, textAlign: "center", transition: "0.15s",
                            ...(clicavel && { cursor: "pointer", "&:hover": { boxShadow: 3, borderColor: cartao.cor } }) }}>
                          <Typography variant="h4" sx={{ color: cartao.cor }}>{valor}</Typography>
                          <Typography variant="body2">{cartao.label}</Typography>
                          <LinearProgress variant="determinate" value={Math.min(100, porcento(valor))}
                            sx={{ mt: 1, height: 6, borderRadius: 3 }} />
                          <Typography variant="caption" color="text.secondary">
                            {mostraPorcento
                              ? `${porcento(valor)}% ${bloco.base === "sent" ? "do enviado" : "de quem abriu"}`
                              : "\u00a0"}
                          </Typography>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            );
          })}

          <Grid container spacing={2}>
            {/* série temporal */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Envios x Respostas por dia</Typography>
                <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
                  <Chip size="small" label="Enviados" sx={{ bgcolor: "#3f51b5", color: "#fff" }} />
                  <Chip size="small" label="Respostas" sx={{ bgcolor: "#2e7d32", color: "#fff" }} />
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, height: 180, overflowX: "auto", pt: 2 }}>
                  {days.length === 0 && <Typography variant="body2" color="text.secondary">Sem dados no período.</Typography>}
                  {days.map((day) => (
                    <Box key={day} sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 28 }}>
                      <Box sx={{ display: "flex", alignItems: "flex-end", gap: "2px", height: 150 }}>
                        <Box title={`Enviados: ${sentByDay[day] || 0}`} sx={{
                          width: 10, height: `${((sentByDay[day] || 0) / maxSeries) * 100}%`,
                          bgcolor: "#3f51b5", borderRadius: "2px 2px 0 0", minHeight: 2 }} />
                        <Box title={`Respostas: ${respByDay[day] || 0}`} sx={{
                          width: 10, height: `${((respByDay[day] || 0) / maxSeries) * 100}%`,
                          bgcolor: "#2e7d32", borderRadius: "2px 2px 0 0", minHeight: 2 }} />
                      </Box>
                      <Typography variant="caption" sx={{ fontSize: 9, transform: "rotate(-45deg)", mt: 1, whiteSpace: "nowrap" }}>
                        {day.slice(5)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <Interessados query={drillQuery.join("&")} />
        </div>
      </div>

      <FunnelDrillDialog
        open={drill.open}
        stage={drill.stage}
        label={drill.label}
        query={drillQuery.join("&")}
        onClose={() => setDrill({ open: false, stage: null, label: "" })}
      />
    </div>
  );
}
